<template>
  <div class="exception-toast-layout" @click.stop>
    <div class="exception-toast-layout__body">
      <span class="exception-toast-layout__title">{{ reason }}</span>
      <span v-if="action || type" class="exception-toast-layout__subtitle">{{ hint }}</span>
    </div>
    <div class="exception-toast-layout__actions">
      <button v-if="showMail" class="exception-toast-layout__action" :title="actionTitle" @click.stop="openMail">
        <i class="mdi mdi-email-outline"></i>
      </button>
      <button class="exception-toast-layout__action" :title="copyTitle" @click.stop="copy">
        <i class="mdi mdi-content-copy"></i>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { HttpException, ForbiddenException, InternalServerErrorException } from "@/lib/exceptions/http";
import { t, te } from "@/lib/utility/t";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { copyToClipboard } from "@/lib/utility/copy-to-clipboard.helper";
import { ConfigModule } from "@/store/modules/config";

@Component
export default class ExceptionToastContent extends Vue {
  @Prop({ required: true }) error!: HttpException;

  @Prop({ required: false }) showMail!: boolean;

  get context() {
    return (this.error as ForbiddenException).data?.context;
  }

  get reason(): string {
    if (this.error instanceof InternalServerErrorException) {
      return t("InternalServerErrorException", { email: ConfigModule.supportMail });
    }
    if (this.error instanceof ForbiddenException) {
      const reason = this.context?.reason;
      if (!reason) return t("enums.ExceptionReasonEnum.default");
      const key = `enums.ExceptionReasonEnum.${reason}`;
      return te(key) ? t(key, this.context) : t("enums.ExceptionReasonEnum.default");
    }
    return this.error.message;
  }

  get action(): string {
    const actions = this.context?.action as ActionEnum[] | undefined;
    if (!actions?.length) return "";
    return actions.map((a) => t(`ActionEnum.verb.${a}`).toLocaleUpperCase()).join(", ");
  }

  get type(): string {
    const type = this.context?.type as BackendResourceEnum | undefined;
    if (!type) return "";
    return t(`BackendResourceEnum.${type}`);
  }

  get originalType(): string {
    return this.context?.type ?? "";
  }

  get copyTitle(): string {
    return t("common.verbs.copy");
  }

  get actionTitle(): string {
    return this.showMail ? ConfigModule.supportMail : t("common.verbs.copy");
  }

  get hint(): string {
    const parts = [this.action, this.type].filter(Boolean).join(" ");
    const suffix = this.originalType ? ` (${this.originalType})` : "";
    return `${t("common.nouns.hint")}: ${parts}${suffix}`;
  }

  copy() {
    const parts = [this.reason];
    if (this.action || this.type) {
      parts.push(
        `${[this.action, this.type].filter(Boolean).join(" ")}${this.originalType ? ` (${this.originalType})` : ""}`
      );
    }
    copyToClipboard(parts.join("\n"));
  }

  openMail() {
    const body = `\n\n____________\n${JSON.stringify(this.error.data, null, 2)}`;
    window.location.href = `mailto:${ConfigModule.supportMail}?body=${encodeURIComponent(body)}`;
  }
}
</script>

<style scoped>
.exception-toast-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.exception-toast-layout__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.exception-toast-layout__title {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: normal;
}

.exception-toast-layout__subtitle {
  font-size: 0.75rem;
  opacity: 0.75;
  white-space: normal;
  font-style: italic;
}

.exception-toast-layout__actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.exception-toast-layout__action {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  opacity: 0.7;
  color: inherit;
  line-height: 1;
}

.exception-toast-layout__action:hover {
  opacity: 1;
}
</style>
